import { withoutEntry } from '@app/utils/common';

interface ListenerOptions<T> {
  /**
   * Called when the first callback is registered with the listener. It
   * should create an event handler and pass the given callback to it.
   *
   * @param callback Callback to pass as the event handler.
   */
  start(callback: (value: T) => void): void;

  /**
   * Called when the last callback is removed from the
   * listener. It should then destroy the event handler.
   *
   * @param callback Callback to pass as the event handler.
   */
  stop(callback: (value: T) => void): void;
}

/**
 * Helper class to keep event listeners to a minimum. Useful when using
 * global event listeners (e.g. keyup, keydown event listeners on
 * document) that should exist only once but are needed in multiple places.
 *
 * A good example is the "portal-reveal-details" component which uses keydown
 * and keyup listeners to determine if the user is currently holding "ctrl" and
 * "alt" to reveal its contents. We don't want to use a listener for each instance
 * but rather one global instance that is then used by every component instance.
 */
export class StoppableListener<T> {
  private _enabled: boolean = false;

  get enabled(): boolean {
    return this._enabled;
  }

  private listeners: ((value: T) => void)[] = [];

  private callback = (value: T) => {
    this.listeners.forEach((listener) => listener(value));
  };

  constructor(private options: ListenerOptions<T>) {}

  /**
   * Add a callback function to the listener. If this is
   * the first callback, then the listener will be created.
   *
   * @param callback Function to add.
   */
  public add(callback: (value: T) => void): void {
    if (this.listeners.length === 0) {
      this.start();
    }

    this.listeners.push(callback);
  }

  /**
   * Remove a callback function from the listener. If this is
   * the last callback, then the listener will be destroyed.
   *
   * @param callback Function to remove.
   */
  public remove(callback: (value: T) => void): void {
    this.listeners = withoutEntry(this.listeners, callback);

    if (this.listeners.length === 0) {
      this.stop();
    }
  }

  /**
   * Start the listener.
   */
  public start(): void {
    if (this._enabled === false) {
      this.options.start(this.callback);
      this._enabled = true;
    }
  }

  /**
   * Stop the listener.
   */
  public stop(): void {
    if (this._enabled) {
      this.options.stop(this.callback);
      this._enabled = false;
    }
  }
}
